import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Chip,
} from '@mui/material';

interface AcademyAttendanceReportPageProps {
  academyId: string;
}

interface PlayerAttendanceReport {
  playerId: string;
  playerIdCode: string;
  name: string;
  sport: string;
  daysAttended: number;
  daysAbsent: number;
  totalDays: number;
  totalRecorded: number;
  attendancePercentage: string;
  attendanceByDate: Record<string, boolean | null>;
}

interface MonthlyReport {
  year: number;
  month: number;
  totalDays: number;
  players: PlayerAttendanceReport[];
  summary: {
    totalPlayers: number;
    averageAttendance: string;
  };
}

const AcademyAttendanceReportPage: React.FC<AcademyAttendanceReportPageProps> = ({ academyId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<MonthlyReport | null>(null);
  
  // Filters
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
  const [selectedSport, setSelectedSport] = useState<string>('all');
  const [selectedPlayer, setSelectedPlayer] = useState<string>('all');

  useEffect(() => {
    loadReport();
  }, [academyId, selectedYear, selectedMonth, selectedSport, selectedPlayer]);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        year: selectedYear.toString(),
        month: selectedMonth.toString(),
      });
      if (selectedSport !== 'all') params.set('sport', selectedSport);
      if (selectedPlayer !== 'all') params.set('playerId', selectedPlayer);
      
      const response = await fetch(`/api/academies/${academyId}/attendance/monthly?${params.toString()}`);
      const result = await response.json();
      
      if (result.success) {
        setReport(result.data);
      } else {
        setError(result.message || 'Failed to load attendance report');
      }
    } catch (err: any) {
      console.error('Error loading attendance report:', err);
      setError('Failed to load attendance report');
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (month: number): string => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1] || '';
  };

  const getAttendanceColor = (percentage: number): 'success' | 'warning' | 'error' => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Monthly Attendance Report
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
        View attendance statistics for players in your academy
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} label="Year">
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Month</InputLabel>
            <Select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))} label="Month">
              {Array.from({ length: 12 }, (_, i) => {
                const month = i + 1;
                return (
                  <MenuItem key={month} value={month}>
                    {getMonthName(month)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sport</InputLabel>
            <Select value={selectedSport} onChange={(e) => setSelectedSport(e.target.value)} label="Sport">
              <MenuItem value="all">All Sports</MenuItem>
              <MenuItem value="Badminton">Badminton</MenuItem>
              <MenuItem value="Cricket">Cricket</MenuItem>
              <MenuItem value="Tennis">Tennis</MenuItem>
              <MenuItem value="Football">Football</MenuItem>
              <MenuItem value="Athletics">Athletics</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : report ? (
        <Box>
          {/* Summary */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.light' }}>
            <Typography variant="h6" gutterBottom>
              Summary - {getMonthName(report.month)} {report.year}
            </Typography>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Total Players</Typography>
                <Typography variant="h5">{report.summary.totalPlayers}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Average Attendance</Typography>
                <Typography variant="h5">{report.summary.averageAttendance}%</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Total Days in Month</Typography>
                <Typography variant="h5">{report.totalDays}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* Report Table */}
          {report.players.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'primary.light' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Player</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Sport</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Days Attended</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Days Absent</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Total Recorded</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Attendance %</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Progress</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.players.map((player) => {
                    const percentage = parseFloat(player.attendancePercentage);
                    return (
                      <TableRow key={player.playerId} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {player.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {player.playerIdCode}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={player.sport} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="success.main" fontWeight="bold">
                            {player.daysAttended}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="error.main" fontWeight="bold">
                            {player.daysAbsent}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {player.totalRecorded} / {player.totalDays}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${player.attendancePercentage}%`}
                            color={getAttendanceColor(percentage)}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={percentage}
                              sx={{
                                flexGrow: 1,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: percentage >= 80 ? 'success.main' : percentage >= 60 ? 'warning.main' : 'error.main',
                                },
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No attendance records found for {getMonthName(report.month)} {report.year}.
            </Alert>
          )}
        </Box>
      ) : (
        <Alert severity="info">Select a month and year to view attendance report.</Alert>
      )}
    </Box>
  );
};

export default AcademyAttendanceReportPage;







